/**
 * c-mega-menu.js
 *
 * WHAT THIS FIXES
 * Horizon's <header-menu> opens a submenu on pointerenter/focus and closes it
 * on pointerleave/blur. That is the whole of its close logic. There is no
 * Escape handler, no click-outside handler, no route-change handler, no
 * click-to-open, and no hover-intent delay. That is the "buggy dropdown"
 * reported on this task: on hybrid/touch laptops a tap never opens the panel
 * reliably, and moving the pointer diagonally between tabs makes it flicker.
 *
 * This file is progressive enhancement layered on top of the vendor component.
 * It does not modify assets/header-menu.js. Every hook is defensive: if the
 * vendor internals change, the menu degrades to exactly today's behaviour
 * rather than breaking.
 *
 * Close conditions covered here: Escape, click outside, tab-out, route change,
 * plus hover-intent delay and click-to-open.
 */
(function () {
  'use strict';

  /**
   * Hover-intent delay before a panel opens.
   *
   * Started at 150ms, which is the textbook value and reads fine on an idle
   * page. It was reported as "the menu doesn't open every time", and this is
   * the most likely cause: the storefront currently takes ~29s to reach
   * domComplete (a 21MB video plus ~10MB of unoptimised imagery), so the main
   * thread is saturated and a 150ms timer regularly lands late or gets starved
   * behind other work. The panel then opens after the pointer has already
   * moved on, which feels like a dropped hover.
   *
   * 60ms was the first step down and it did fix the dropped-hover reports, but
   * measurement showed it had gone one step too far: sweeping FEATURED ->
   * ACCESSORIES at 4-16ms per pointer step still transiently opened ~2
   * intermediate panels per sweep, which is the exact flicker the delay exists
   * to suppress.
   *
   * Swept delay against pointer speed, 6 runs per cell, counting opens of
   * INTERMEDIATE panels only (mean per sweep):
   *
   *            4ms/step  8ms/step  16ms/step  30ms/step  50ms/step
   *     60ms      2.0       2.0       2.2        3.0        3.0
   *     80ms      0.2       0.0       1.3        3.0        3.0
   *    100ms      0.0       0.0       0.2        2.3        3.0
   *    120ms      0.0       0.0       0.0        2.0        3.0
   *
   * 80ms is the first step that clears the fast-cross flicker. The destination
   * panel opened 6/6 in every cell, so this costs nothing in open reliability
   * (separately measured at 80/80 opens across 4 tabs x 20 rounds). The
   * 30-50ms/step columns are not flicker — at that speed the pointer genuinely
   * dwells longer than any of these delays, so opening is correct.
   *
   * Raise in 20ms steps if flicker returns; do not remove it. With no delay at
   * all, crossing TOPS on the way to ACCESSORIES opens and closes TOPS on the
   * way past.
   *
   * This does not remove the underlying cause. The page weight is the real
   * problem and is tracked separately.
   */
  var C_HOVER_INTENT_MS = 80;
  var C_CLOSED_ATTR = 'data-c-closed';

  /**
   * Wire one <header-menu> element.
   * @param {HTMLElement} menu
   */
  function c_enhance(menu) {
    if (!menu || menu.dataset.cEnhanced === 'true') return;
    menu.dataset.cEnhanced = 'true';

    var c_openTimer = null;
    var c_header = menu.closest('header-component') || document.querySelector('header-component');

    function c_cancelOpenTimer() {
      if (c_openTimer !== null) {
        window.clearTimeout(c_openTimer);
        c_openTimer = null;
      }
    }

    function c_clearForcedClose() {
      if (menu.hasAttribute(C_CLOSED_ATTR)) menu.removeAttribute(C_CLOSED_ATTR);
    }

    function c_isOpen() {
      return menu.getAttribute('aria-expanded') === 'true';
    }

    /* ----------------------------------------------------------------
     * Hover intent
     * Wrap the vendor's public `activate` so a pointer merely crossing a
     * tab on its way somewhere else does not open a panel.
     * ---------------------------------------------------------------- */
    if (typeof menu.activate === 'function') {
      var c_originalActivate = menu.activate.bind(menu);

      menu.activate = function (event) {
        c_clearForcedClose();

        // Keyboard focus and touch should open immediately; only defer pointer hover.
        if (!event || event.type !== 'pointerenter') {
          c_cancelOpenTimer();
          return c_originalActivate(event);
        }

        c_cancelOpenTimer();
        c_openTimer = window.setTimeout(function () {
          c_openTimer = null;
          // The pointer may have left during the delay. Opening now would show
          // a panel for a tab the visitor is no longer on.
          if (event.target instanceof Element && !event.target.matches(':hover')) return;
          c_clearForcedClose();
          c_originalActivate(event);
        }, C_HOVER_INTENT_MS);
      };
    }

    /* ----------------------------------------------------------------
     * Hard close
     * The vendor's #deactivate bails out while the submenu is hovered, and
     * its CSS keeps a hovered panel visible regardless. So a forced close
     * also sets an attribute that CSS uses to hide the panel outright. The
     * attribute is cleared on the next real pointer/focus interaction.
     * ---------------------------------------------------------------- */
    function c_forceClose() {
      c_cancelOpenTimer();
      menu.setAttribute(C_CLOSED_ATTR, 'true');

      try {
        if (typeof menu.deactivate === 'function') {
          menu.deactivate({ target: menu, relatedTarget: null, type: 'blur' });
        }
      } catch (e) {
        /* vendor internals changed - the CSS attribute still closes it */
      }

      if (c_header) {
        c_header.style.setProperty('--submenu-height', '0px');
        c_header.style.setProperty('--full-open-header-height', '0px');
      }
      menu.style.setProperty('--submenu-opacity', '0');
      menu.setAttribute('aria-expanded', 'false');

      var c_expanded = menu.querySelectorAll('[aria-expanded="true"]');
      for (var i = 0; i < c_expanded.length; i++) {
        c_expanded[i].setAttribute('aria-expanded', 'false');
      }
    }

    /* ---------------------------- Escape ---------------------------- */
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' && event.key !== 'Esc') return;
      if (!c_isOpen()) return;

      var c_active = document.activeElement;
      var c_tab = null;
      if (c_active && menu.contains(c_active) && typeof c_active.closest === 'function') {
        c_tab = c_active.closest('.menu-list__list-item');
      }

      c_forceClose();

      // Return focus to the tab the visitor was on, so keyboard users are not dumped at the top.
      var c_link = c_tab && c_tab.querySelector('[ref="menuitem"]');
      if (c_link && typeof c_link.focus === 'function') c_link.focus();
    });

    /* ------------------------- Click outside ------------------------ */
    document.addEventListener('pointerdown', function (event) {
      if (!c_isOpen()) return;
      var c_target = event.target;
      if (!(c_target instanceof Node)) return;
      if (menu.contains(c_target)) return;
      if (c_header && c_header.contains(c_target)) return;
      c_forceClose();
    });

    /* --------------------------- Tab out ----------------------------
     * Only a genuine tab-out should close the panel.
     *
     * The naive version of this closed the menu whenever focus went
     * nowhere — and pressing a <button> inside the panel does exactly
     * that on Safari and Firefox, where a mouse click does not move
     * focus to a button. That is the same fault that made the slider
     * arrows dismiss the menu. So focus landing on <body> is treated as
     * "focus went nowhere", not "focus left the menu", and the panel is
     * left alone while the pointer is still inside it.
     * ---------------------------------------------------------------- */
    menu.addEventListener('focusout', function (event) {
      if (!c_isOpen()) return;

      var c_next = event.relatedTarget;
      if (c_next instanceof Node && menu.contains(c_next)) return;

      window.setTimeout(function () {
        var c_active = document.activeElement;

        // Focus moved somewhere real inside the menu — nothing to do.
        if (c_active && menu.contains(c_active)) return;

        // Focus went nowhere at all. Not a tab-out.
        if (!c_active || c_active === document.body || c_active === document.documentElement) return;

        // Pointer is still in the menu, so the visitor has not left it.
        if (menu.matches(':hover')) return;

        c_forceClose();
      }, 0);
    });

    /* ------------------------- Route change ------------------------- */
    window.addEventListener('popstate', c_forceClose);
    window.addEventListener('pagehide', c_forceClose);
    window.addEventListener('hashchange', c_forceClose);
    document.addEventListener('shopify:section:load', c_forceClose);

    /* ------------- Restore normal behaviour on interaction ----------
     * pointerenter is included alongside pointermove because a fast pointer
     * can enter a tab and trigger activate before any pointermove fires. If
     * the forced-close attribute is still set at that moment the vendor opens
     * the panel but CSS keeps it hidden — a panel that is "open" and invisible,
     * which is another route to the reported flakiness.
     * ---------------------------------------------------------------- */
    menu.addEventListener('pointerenter', c_clearForcedClose, true);
    menu.addEventListener('pointermove', c_clearForcedClose);
    menu.addEventListener('focusin', c_clearForcedClose);

    /* ------------------------ Click to open -------------------------
     * Hover-only breaks on hybrid laptops that report touch but use a
     * trackpad. A top-level tab that has a submenu opens on first click and
     * navigates on the second, so the parent collection stays reachable.
     * ---------------------------------------------------------------- */
    menu.addEventListener('click', function (event) {
      var c_target = event.target;
      if (!(c_target instanceof Element)) return;

      var c_link = c_target.closest('[ref="menuitem"]');
      if (!c_link) return;
      if (c_link.getAttribute('aria-haspopup') !== 'true') return;

      // If this tab's panel is already open, let the click through to the collection.
      if (c_link.getAttribute('aria-expanded') === 'true') return;

      event.preventDefault();
      c_clearForcedClose();
      c_cancelOpenTimer();

      if (typeof menu.activate === 'function') {
        menu.activate({ target: c_link.parentElement || c_link, type: 'click', slot: '' });
      }
    });

    /* ---------------------- Reduced motion -------------------------- */
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      menu.dataset.cReducedMotion = 'true';
    }
  }

  /**
   * Lock body scroll while the mobile drawer is open, and restore the
   * scroll position on close. Horizon does not do this, so the page behind
   * the drawer scrolls under the visitor's finger.
   *
   * The selector matters. This previously queried
   * [data-header-drawer-type="mobile-drawer"], an attribute that exists
   * NOWHERE — not in the rendered output, not in any Liquid file in this repo.
   * So the query returned null, the function bailed on its first line, and the
   * scroll lock never wired at all. It read as working code for weeks because
   * the failure is silent and the symptom (the page scrolling behind the open
   * drawer) looks like something Horizon just doesn't do.
   *
   * details.menu-drawer-container is the element header-drawer.js actually
   * toggles `open` on, which is also what the MutationObserver below watches
   * for. Verified in a browser: locks on open, restores the scroll position on
   * close, and survives drilling into a submenu and back.
   */
  function c_wireDrawerScrollLock() {
    var c_drawer = document.querySelector('details.menu-drawer-container');
    if (!c_drawer || c_drawer.dataset.cScrollLock === 'true') return;
    c_drawer.dataset.cScrollLock = 'true';

    var c_scrollY = 0;

    var c_observer = new MutationObserver(function () {
      var c_open = c_drawer.hasAttribute('open') || c_drawer.getAttribute('aria-hidden') === 'false';

      if (c_open && document.body.dataset.cScrollLocked !== 'true') {
        c_scrollY = window.scrollY;
        document.body.dataset.cScrollLocked = 'true';
        document.body.style.position = 'fixed';
        document.body.style.top = '-' + c_scrollY + 'px';
        document.body.style.width = '100%';
      } else if (!c_open && document.body.dataset.cScrollLocked === 'true') {
        delete document.body.dataset.cScrollLocked;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, c_scrollY);
      }
    });

    c_observer.observe(c_drawer, { attributes: true, attributeFilter: ['open', 'aria-hidden'] });
  }

  /**
   * Start the mobile drawer's images loading before the drawer is on screen.
   *
   * THE PROBLEM (reported on MarkUp: "on mobile it takes some time for these
   * collection images on page 2 to load")
   * Every image in the drawer is loading="lazy", and the drill-down panels are
   * `visibility: hidden` inside a closed <details>. A lazy image in a hidden
   * container is never near the viewport, so the browser does not fetch it
   * until the panel is revealed — at which point the visitor is looking at
   * empty tiles while the request goes out.
   *
   * WHY NOT JUST RENDER THEM EAGER
   * There are four panels, each with up to eight tiles plus five product
   * thumbnails. Rendering that eager would put ~50 images the visitor may
   * never see into the initial page load, on a storefront the build notes
   * already flag as taking ~29s to domComplete. That trades one complaint for
   * a worse one.
   *
   * WHAT THIS DOES
   * Flips loading to eager at the first sign the visitor is going for the menu
   * — pointerdown on the hamburger, which lands before the click, and again on
   * open as a backstop for keyboard and programmatic opens. Per the HTML spec,
   * moving an image out of the lazy state resumes its load immediately, and an
   * eager image loads even inside a hidden or display:none container. That
   * buys the pointerdown-to-click gap plus the 300ms open animation before the
   * panel is actually looked at.
   *
   * fetchpriority is deliberately not set: ~50 high-priority requests would
   * contend with each other and with whatever the page is still loading.
   */
  function c_eagerImages(c_root) {
    if (!c_root || c_root.dataset.cEager === 'true') return;
    c_root.dataset.cEager = 'true';

    var c_imgs = c_root.querySelectorAll('img[loading="lazy"]');
    for (var i = 0; i < c_imgs.length; i++) c_imgs[i].loading = 'eager';
  }

  function c_wireDrawerImages() {
    /* Same element and same selector as c_wireDrawerScrollLock above — the
       <details> header-drawer.js toggles `open` on. See the note there for why
       it is not [data-header-drawer-type="mobile-drawer"]. */
    var c_details = document.querySelector('details.menu-drawer-container');
    if (!c_details || c_details.dataset.cEagerWired === 'true') return;
    c_details.dataset.cEagerWired = 'true';

    var c_drawer = c_details.querySelector('.menu-drawer') || c_details;

    var c_summary = c_details.querySelector('summary');
    if (c_summary) {
      c_summary.addEventListener(
        'pointerdown',
        function () {
          c_eagerImages(c_drawer);
        },
        { passive: true }
      );
    }

    /* Backstop: keyboard activation and any programmatic open skip
       pointerdown, and the theme editor can re-render the drawer open. */
    var c_observer = new MutationObserver(function () {
      if (c_details.hasAttribute('open')) c_eagerImages(c_drawer);
    });
    c_observer.observe(c_details, { attributes: true, attributeFilter: ['open'] });

    if (c_details.hasAttribute('open')) c_eagerImages(c_drawer);
  }

  function c_init() {
    var c_menus = document.querySelectorAll('header-menu');
    for (var i = 0; i < c_menus.length; i++) c_enhance(c_menus[i]);
    c_wireDrawerScrollLock();
    c_wireDrawerImages();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', c_init);
  } else {
    c_init();
  }

  // The header is a section: re-wire after a theme-editor reload.
  document.addEventListener('shopify:section:load', function () {
    window.setTimeout(c_init, 0);
  });
})();
